<template>
  <v-row justify="center" align="center">
    <v-col cols="12" lg="8" xl="6">
      <h2 align="center" class="title-underline" translate>
        {{ $t(["REFUNDS"]) }}
      </h2>

      <v-alert
        v-model="errorMsgAlert"
        close-text="Close Alert"
        class="alert-text mb-5"
        color="error"
        dark
        dismissible
        ref="snackbarError"
      >
        {{ $t(errorMsg) }}
      </v-alert>

      <v-snackbar
        v-model="snackbar"
        class="mt-15"
        color="success"
        :snackbarTimeout="snackbarTimeout"
        top
      >
        <div class="text-center py-1" style="font-size: 16px">
          {{ $t(snackbarText) }}
        </div>

        <template v-slot:action="{ attrs }">
          <v-btn color="white" icon v-bind="attrs" @click="snackbar = false">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <div class="text-center last-updated-text my-3">
        <span> {{ $t(["Last Updated"]) }} {{ lastUpdatedTime }} </span>
      </div>

      <v-card class="card mb-10">
        <div class="float-right pt-3 pr-3">
          <v-btn
            color="secondary"
            fab
            top
            right
            small
            :loading="refreshBtnLoading"
            @click="getAllRefunds()"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>

        <v-row class="px-8 py-5 centered-input" justify="center">
          <v-chip-group
            v-model="orderTableFilter"
            class=""
            justify="center"
            active-class="primary"
            mandatory
            column
          >
            <v-chip class="" filter>
              {{ $t(["Pending"]) }} ({{ filteredItems[0].length }})
            </v-chip>

            <!-- <div placeholder="{{ 'Approved' | translate }}"></div> -->
            <v-chip class="" filter>
              {{ $t(["Approved"]) }} ({{ filteredItems[1].length }})
            </v-chip>
            <v-chip class="" filter>
              {{ $t(["All Records"]) }} ({{ filteredItems[2].length }})
            </v-chip>
          </v-chip-group>
        </v-row>

        <v-divider class="mb-3 mx-3"></v-divider>

        <v-row justify="center" class="px-8 pb-5">
          <div style="width: 500px" class="mt-0">
            <v-text-field
              v-model="search"
              type="tel"
              class="centered-input mt-0"
              single-line
              hide-details
              v-mask="'(###) ###-####'"
            >
              <template v-slot:label>
                {{ $t(["Search"]) }}

                <v-icon style="vertical-align: middle"> mdi-magnify </v-icon>
              </template>
            </v-text-field>
          </div>
        </v-row>

        <v-divider class="mt-3 mx-3"></v-divider>

        <template>
          <v-data-table
            class="px-3"
            v-model="selectedRow"
            item-key="id"
            mobile-breakpoint="0"
            :loading="refundTableLoading"
            :loading-text="$t(['Loading items'])"
            :no-data-text="$t(['No data available'])"
            :headers="headers"
            :items="filteredItems[orderTableFilter]"
            :search="search"
            hide-default-footer
            disable-pagination
            @click:row="rowClick"
          >
            <template v-slot:item.dt="{ item }">
              <span>{{
                $i18n.locale == "fr"
                  ? formatDt(item.dt)
                      .locale($i18n.locale)
                      .format("D MMMM, YYYY")
                  : formatDt(item.dt)
                      .locale($i18n.locale)
                      .format("MMMM D, YYYY")
              }}</span>
            </template>

            <template v-slot:item.refundAmountText="{ item }">
              <span
                v-html="
                  '(' + $t('Currency', { amount: item.refundAmountText }) + ')'
                "
                class="secondary--text"
              />
            </template>

            <template v-slot:item.status="{ item }">
              <span>{{ $t(item.status) }}</span>
            </template>

            <template v-slot:item.closed="{ item }">
              <span>{{
                item.closed
                  ? $i18n.locale == "fr"
                    ? formatDt(item.closed)
                        .locale($i18n.locale)
                        .format("D MMMM, YYYY")
                    : formatDt(item.closed)
                        .locale($i18n.locale)
                        .format("MMMM D, YYYY")
                  : ""
              }}</span>
            </template>
          </v-data-table>
        </template>
      </v-card>

      <v-row justify="center" align="center">
        <v-col cols="10" md="8" lg="8" xl="6">
          <v-btn class="btn-text" x-large block @click="backToHome()">
            {{ $t(["Back"]) }}
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { Auth } from "aws-amplify";
import moment from "moment";

import { useAuthenicationStore } from "../../store/authenication";
import { useRefundStore } from "../../store/refund";

export default {
  name: "RefundOverview",
  data: () => ({
    selectedRow: [1],
    search: "",
    orderTableFilter: 0,
    refreshBtnLoading: false,
    refundTableLoading: false,
    lastUpdatedTime: "",
    updatedTime: "",
    interval: null,
    errorMsgAlert: false,
    errorMsg: "",
    snackbar: false,
    snackbarText: "",
    snackbarTimeout: 3000,
    filteredItems: [[], [], []],
  }),
  computed: {
    headers() {
      return [
        {
          text: this.$t("Order Number"),
          align: "start",
          sortable: false,
          value: "orderNumberId",
        },
        {
          text: this.$t("Requested Date"),
          align: "start",
          sortable: false,
          value: "dt",
        },
        { text: this.$t("Amount"), sortable: false, value: "refundAmountText" },
        { text: this.$t("Status"), sortable: false, value: "status" },
        {
          text: this.$t("Closed Date"),
          sortable: false,
          value: "closed",
        },
      ];
    },
  },
  setup() {
    const authStore = useAuthenicationStore();
    const refundStore = useRefundStore();

    return {
      authStore,
      refundStore,
    };
  },
  destroyed() {
    clearInterval(this.interval);
  },
  mounted() {
    this.getAllRefunds();

    this.snackbar = this.$route.params.success;
    this.snackbarText = this.$route.params.message;
  },
  methods: {
    formatDt(dt) {
      return moment(new Date(dt));
    },
    async getAllRefunds() {
      this.refreshBtnLoading = true;
      this.refundTableLoading = true;

      try {
        await Auth.currentSession().then(async (data) => {
          await this.refundStore
            .getAllRefundsApi(
              {
                eventName: this.authStore.runSiteInfo.eventName,
              },
              data.idToken.jwtToken
            )
            .then(async (response) => {
              // console.log(response);

              if (response.success) {
                this.updatedTime = new Date();

                this.interval = setInterval(() => {
                  let momentObject = moment(this.updatedTime).locale(
                    this.$i18n.locale
                  );
                  this.lastUpdatedTime = momentObject.fromNow();
                }, 1000);

                let allRefunds = response.allRefunds;

                for (const item of allRefunds) {
                  await this.getAllOrderRefunds(item);
                }

                let sortedTime = allRefunds.sort((x, y) => {
                  return y.modified - x.modified;
                });

                this.applyFilter(sortedTime);
              } else {
                throw response.message;
              }
            });
        });
      } catch (error) {
        this.errorMsgAlert = true;
        this.errorMsg = error.message == undefined ? error : error.message;

        this.$nextTick(() => {
          const el = this.$el.querySelector("snackbarError");
          this.$vuetify.goTo(-5, el);

          return;
        });
      }

      this.refreshBtnLoading = false;
      this.refundTableLoading = false;
    },
    applyFilter(allRefunds) {
      allRefunds.forEach(async (item) => {
        item.refundAmountText = item.amount.total.toFixed(2);
      });

      this.filteredItems[0] = allRefunds.filter((item) => {
        if (item.status == "Pending") {
          return item;
        }
      });

      this.filteredItems[1] = allRefunds.filter((item) => {
        if (item.status == "Approved") {
          return item;
        }
      });

      this.filteredItems[2] = allRefunds;
    },
    async rowClick(item, row) {
      let data = item;

      this.refundStore.setRefundData(item);

      this.$router.push({
        name: "RefundDetails",
        params: { data: data },
      });
    },
    backToHome() {
      this.$router.push("/home");
    },
    async getAllOrderRefunds(refundInfo) {
      try {
        await Auth.currentSession().then(async (data) => {
          await this.refundStore
            .getAllOrderRefundsApi(
              {
                eventName: this.authStore.runSiteInfo.eventName,
                orderId: refundInfo.orderId,
              },
              data.idToken.jwtToken
            )
            .then((response) => {
              // console.log(response);

              if (response.success) {
                let sortedTime = response.orderRefunds.sort((x, y) => {
                  return y.modified - x.modified;
                });

                refundInfo.orderRefunds = sortedTime;
              } else {
                throw response.message;
              }
            });
        });
      } catch (error) {
        this.errorMsgAlert = true;
        this.errorMsg = error.message == undefined ? error : error.message;

        // Scroll to first error
        this.$nextTick(() => {
          const el = this.$el.querySelector("snackbarError");
          this.$vuetify.goTo(-5, el);

          return;
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.last-updated-text {
  font-size: 14px;
  font-weight: bold;
}
</style>
